import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../../services/api";
import { UserType } from "../../types/UserType";

export type AuthContextType = {
  user: UserType | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (token: string, user: UserType) => void;
  logout: () => void;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadStorageData = async () => {
      const storedUser = localStorage.getItem("user");
      const storedToken = localStorage.getItem("token");

      console.log("AuthProvider: Loading storage data", { storedUser, storedToken });

      if (storedUser && storedToken) {
        try {
          const parsedUser = JSON.parse(storedUser);
          console.log("AuthProvider: Parsed user", parsedUser);

          if (parsedUser) {
            // Definir o token nos headers padrão
            api.defaults.headers.Authorization = `Bearer ${storedToken}`;

            // Validar token com backend enviando o header explicitamente
            const response = await api.post('/auth/validate', {}, {
              headers: {
                Authorization: `Bearer ${storedToken}`
              }
            });

            const validatedUser = response.data.data;
            const userPerfis = response.data.perfis;

            // Mesclar perfis no objeto de usuário
            const fullUser = { ...validatedUser, perfis: userPerfis };

            setUser(fullUser);
            // Atualizar localStorage com dados mais recentes
            localStorage.setItem("user", JSON.stringify(fullUser));
          } else {
            console.warn("AuthProvider: Parsed user is null or invalid");
            logout();
          }
        } catch (error: any) {
          console.error("Falha ao restaurar sessão ou token inválido", error.response?.data || error.message);
          logout();
        }
      } else {
        console.log("AuthProvider: Missing user or token in localStorage");
      }
      setLoading(false);
    };

    loadStorageData();
  }, []);

  const login = (token: string, userData: UserType) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));

    api.defaults.headers.Authorization = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete api.defaults.headers.Authorization;
    setUser(null);
    // window.location.href = "/login"; // Opcional: redirecionar
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};
